import { r, s } from '@/theme/tokens';
import { Box, Skeleton, Table, TableBody, TableCell, TableHead, TableRow } from '@/ui/primitives';
import {
  HEADER_SEPARATOR_SX,
  resolveHistoryDayHeaderFallbackInterval,
  shouldShowDayHeaderLabel,
} from './EntityDailyHistoryPanel.constants';
import type { ReactElement } from 'react';

type EntityDailyHistoryPanelSkeletonProps = {
  dayCount: number;
  rowCount: number;
  nameColumnWidth: string;
  rowHeight: number;
  blockSize: number;
  headerHeight: number;
};

const HISTORY_SKELETON_RADIUS = '999px';
const HISTORY_TEXT_SKELETON_HEIGHT = 14;
const HISTORY_ROW_NAME_SKELETON_HEIGHT = 16;
const HISTORY_DATE_SKELETON_HEIGHT = 14;
const HISTORY_STATUS_BLOCK_SKELETON_HEIGHT = 18;
const HEADER_NAME_WIDTH = '76px';
const HEADER_STATUS_WIDTH = '84px';
const HEADER_DATE_WIDTH = '38px';
const ROW_NAME_WIDTHS = ['72%', '64%', '68%', '70%', '62%'] as const;

export const EntityDailyHistoryPanelSkeleton = ({
  dayCount,
  rowCount,
  nameColumnWidth,
  rowHeight,
  blockSize,
  headerHeight,
}: EntityDailyHistoryPanelSkeletonProps): ReactElement => {
  const headerInterval = resolveHistoryDayHeaderFallbackInterval(dayCount);

  return (
    <Box>
      <Table
        aria-label="Loading history"
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: 0,
        }}
      >
        <colgroup>
          <col width={nameColumnWidth} />
          <col width="100%" />
        </colgroup>
        <TableHead>
          <TableRow
            sx={{
              height: `${headerHeight}px`,
            }}
          >
            <TableCell
              sx={[
                {
                  width: nameColumnWidth,
                  minWidth: nameColumnWidth,
                  maxWidth: nameColumnWidth,
                  px: s('md'),
                  pt: `calc(${s('md')} + 2px)`,
                  pb: s('none'),
                  verticalAlign: 'top',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  position: 'relative',
                },
                HEADER_SEPARATOR_SX,
              ]}
            >
              <Skeleton
                variant="rounded"
                width={HEADER_NAME_WIDTH}
                sx={{
                  height: `${HISTORY_TEXT_SKELETON_HEIGHT}px`,
                  transform: 'none',
                  borderRadius: HISTORY_SKELETON_RADIUS,
                }}
              />
            </TableCell>
            <TableCell
              sx={{
                px: s('md'),
                pt: `calc(${s('md')} + 2px)`,
                pb: s('none'),
                verticalAlign: 'top',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  height: `calc(${headerHeight}px - ${s('md')} - 2px)`,
                }}
              >
                <Skeleton
                  variant="rounded"
                  width={HEADER_STATUS_WIDTH}
                  sx={{
                    height: `${HISTORY_TEXT_SKELETON_HEIGHT}px`,
                    transform: 'none',
                    borderRadius: HISTORY_SKELETON_RADIUS,
                    flexShrink: 0,
                  }}
                />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${String(dayCount)}, minmax(0, 1fr))`,
                    columnGap: s('md'),
                    alignItems: 'center',
                    width: '100%',
                    minWidth: 0,
                    mt: `calc(${s('md')} + 3px)`,
                    pb: s('none'),
                  }}
                >
                  {Array.from({ length: dayCount }, (_unused, index) => (
                    <Box
                      key={`history-skeleton-header-day-${String(index)}`}
                      sx={{
                        position: 'relative',
                        width: '100%',
                        minWidth: 0,
                        height: `${HISTORY_DATE_SKELETON_HEIGHT}px`,
                        overflow: 'visible',
                      }}
                    >
                      {shouldShowDayHeaderLabel(index, dayCount - 1, headerInterval) ? (
                        <Skeleton
                          variant="rounded"
                          width={HEADER_DATE_WIDTH}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            height: `${HISTORY_DATE_SKELETON_HEIGHT}px`,
                            transform: 'none',
                            borderRadius: HISTORY_SKELETON_RADIUS,
                          }}
                        />
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow
            sx={{
              height: `calc(${s('sm')} - 1px)`,
            }}
          >
            <TableCell
              colSpan={2}
              sx={{
                p: s('none'),
                borderBottom: 'none',
              }}
            />
          </TableRow>
          {Array.from({ length: rowCount }, (_unused, rowIndex) => (
            <TableRow
              key={`history-skeleton-row-${String(rowIndex)}`}
              sx={{
                height: `${rowHeight}px`,
              }}
            >
              <TableCell
                sx={[
                  {
                    width: nameColumnWidth,
                    minWidth: nameColumnWidth,
                    maxWidth: nameColumnWidth,
                    px: s('md'),
                    py: '1px',
                    verticalAlign: 'middle',
                    borderBottom: 'none',
                  },
                ]}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: s('sm'), minWidth: 0 }}>
                  <Skeleton
                    variant="rounded"
                    width={ROW_NAME_WIDTHS[rowIndex % ROW_NAME_WIDTHS.length]}
                    sx={{
                      height: `${HISTORY_ROW_NAME_SKELETON_HEIGHT}px`,
                      transform: 'none',
                      borderRadius: HISTORY_SKELETON_RADIUS,
                      flexShrink: 0,
                    }}
                  />
                </Box>
              </TableCell>
              <TableCell
                sx={[
                  {
                    px: s('md'),
                    py: '1px',
                    verticalAlign: 'middle',
                    borderBottom: 'none',
                    width: '100%',
                  },
                ]}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${String(dayCount)}, minmax(0, 1fr))`,
                    columnGap: s('md'),
                    alignItems: 'center',
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  {Array.from({ length: dayCount }, (_dayValue, dayIndex) => (
                    <Skeleton
                      key={`history-skeleton-cell-${String(rowIndex)}-${String(dayIndex)}`}
                      variant="rounded"
                      sx={{
                        width: '100%',
                        minWidth: `${blockSize}px`,
                        height: `${HISTORY_STATUS_BLOCK_SKELETON_HEIGHT}px`,
                        transform: 'none',
                        borderRadius: r('sm'),
                      }}
                    />
                  ))}
                </Box>
              </TableCell>
            </TableRow>
          ))}
          <TableRow
            sx={{
              height: s('sm'),
            }}
          >
            <TableCell
              colSpan={2}
              sx={{
                p: s('none'),
                borderBottom: 'none',
              }}
            />
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};
